import { type FC, useCallback, useState } from "react";

import type { DashboardCreditTransaction } from "../../dashboard-client";

import { useBilling } from "../../hooks/useBilling";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { formatUsd } from "../../utils/currency";
import { isAdmin } from "../../utils/rbac";
import { formatDateTime } from "../../utils/time";
import { Alert } from "../common/Alert";
import { type BadgeVariant, Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { LoadingState } from "../common/LoadingState";
import { PanelSection } from "../common/PanelSection";
import { Text } from "../common/Text";

const PRESET_AMOUNTS = [
  { cents: 1000, label: "$10" },
  { cents: 2500, label: "$25" },
  { cents: 5000, label: "$50" },
  { cents: 10_000, label: "$100" },
];

const TRANSACTION_TYPE_CONFIG: Record<
  string,
  { label: string; variant: BadgeVariant }
> = {
  deduction: { label: "Usage", variant: "info" },
  expiry: { label: "Expired", variant: "warning" },
  grant: { label: "Grant", variant: "success" },
  topup: { label: "Top-Up", variant: "success" },
};

function TransactionRow({ tx }: { tx: DashboardCreditTransaction }) {
  const isCredit = tx.amountUsd > 0;
  const config = TRANSACTION_TYPE_CONFIG[tx.type];
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-2 last:border-0 dark:border-gray-700">
      <div className="flex-1">
        <Badge variant={config.variant} size="sm">
          {config.label}
        </Badge>
        {tx.description && (
          <Text as="p" className="mt-0.5 max-w-xs truncate">
            {tx.description}
          </Text>
        )}
      </div>
      <div className="text-right">
        <span
          className={`font-mono text-sm font-medium ${isCredit ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
        >
          {isCredit ? "+" : ""}
          {formatUsd(tx.amountUsd)}
        </span>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {formatDateTime(new Date(tx.createdAt).getTime())}
        </p>
      </div>
    </div>
  );
}

export const BillingPanel: FC = () => {
  const auth = useDashboardAuth();
  const {
    balance,
    hasBillingProfile,
    transactions,
    loading,
    error,
    setup,
    topUp,
    openPortal,
  } = useBilling(auth.organizationId);

  const canManage = isAdmin(auth.orgRole, auth.globalRole);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleAction = useCallback(
    async (name: string, action: () => Promise<void>) => {
      setActionError(null);
      setActionLoading(name);
      try {
        await action();
      } catch (_error) {
        setActionError(
          _error instanceof Error ? _error.message : `Failed to ${name}`,
        );
      } finally {
        setActionLoading(null);
      }
    },
    [],
  );

  if (!auth.organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        description="Select an organization to view billing information."
      />
    );
  }

  if (loading) {
    return <LoadingState message="Loading billing..." />;
  }

  if (error) {
    return <Alert variant="error">{error}</Alert>;
  }

  const showLowBalanceWarning = balance !== null && balance > 0 && balance < 5;
  const showZeroBalanceWarning = balance !== null && balance <= 0;

  return (
    <div className="space-y-4">
      {actionError && <Alert variant="error">{actionError}</Alert>}

      <PanelSection title="Credit Balance">
        <span className="text-3xl font-bold text-gray-900 dark:text-white">
          {formatUsd(balance)}
        </span>

        {showZeroBalanceWarning && (
          <Alert variant="error" className="mt-3">
            Your balance is depleted. Top up to continue submitting tasks.
          </Alert>
        )}
        {showLowBalanceWarning && (
          <Alert variant="warning" className="mt-3">
            Your balance is low. Consider topping up soon.
          </Alert>
        )}
      </PanelSection>

      {canManage && (
        <PanelSection title="Top Up">
          {!hasBillingProfile ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enable your billing profile before starting credit top-ups.
              </p>
              <Button
                variant="primary"
                onClick={() => void handleAction("set up billing", setup)}
                loading={actionLoading === "set up billing"}
                loadingText="Setting up..."
              >
                Set Up Billing
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {PRESET_AMOUNTS.map(({ cents, label }) => (
                <Button
                  key={cents}
                  variant="primary"
                  onClick={() =>
                    void handleAction(`top-up-${cents}`, () => topUp(cents))
                  }
                  loading={actionLoading === `top-up-${cents}`}
                  loadingText="..."
                >
                  {label}
                </Button>
              ))}
            </div>
          )}
        </PanelSection>
      )}

      {canManage && hasBillingProfile && (
        <PanelSection title="Payment Methods">
          <Button
            variant="secondary"
            onClick={() => void handleAction("open portal", openPortal)}
            loading={actionLoading === "open portal"}
            loadingText="Opening..."
          >
            Manage Payment Methods
          </Button>
        </PanelSection>
      )}

      <PanelSection title="Recent Transactions">
        {transactions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No transactions yet.
          </p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {transactions.map((tx) => (
              <TransactionRow key={tx.id} tx={tx} />
            ))}
          </div>
        )}
      </PanelSection>
    </div>
  );
};
